import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import LoadingSpinner from "../components/LoadingSpinner";
import PhotosViewer from "../components/PhotosViewer";
import ReportMaintenanceButton from "../components/ReportMaintenanceButton";
import { DetailCard, DetailRow } from "../components/signal/DetailCard";
import BoxNotesCard from "../components/signal/BoxNotesCard";
import ReaderCard from "../components/signal/ReaderCard";
import RebootCard from "../components/signal/RebootCard";
import TicketHistoryCard from "../components/signal/TicketHistoryCard";
import BandwidthCard from "../components/BandwidthCard";
import PeplinkClientsCard from "../components/peplink/PeplinkClientsCard";
import SimControlCard from "../components/SimControlCard";
import PlugOutletResetButton from "../components/plugs/PlugOutletResetButton";
import StarlinkCard from "../components/signal/StarlinkCard";
import UnmatchedTagsCard from "../components/signal/UnmatchedTagsCard";
import SignalDot from "../components/SignalDot";
import StateBadge from "../components/StateBadge";
import TicketActivity from "../components/TicketActivity";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { fmtCustomerWc, fmtDuration, fmtUtc } from "../lib/format";
import { deriveSignalDots } from "../lib/signalDots";
import {
  type BoxScanDetail,
  fetchBox,
  fetchScanDetail,
  readerGroups,
} from "../services/boxDetailApi";
import type { Box } from "../types/boxes";

/**
 * One-SCU detail view. Mirrors the CLI's `mttr signal SCU` output
 * structurally — the same data points, organised into "Identity",
 * "Classification", "Ticket", "Position".
 *
 * For the deep signal trace (per-source timestamps, threshold breakdown,
 * down-reason list) you still want the CLI:
 *     python -m mttr signal <scu>
 * The frontend doesn't have that depth in v1; the JSON snapshot only
 * carries top-level state, not per-signal details.
 */
export const SignalDetailPage: React.FC = () => {
  const { scuName } = useParams<{ scuName: string }>();
  const { token } = useAuth();

  // Fetch only THIS box instead of the whole fleet snapshot just to `.find()`
  // one row. Same per-box object shape, scoped server-side.
  const [box, setBox] = useState<Box | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!scuName || !token) return;
    let cancelled = false;
    setLoading(true);
    fetchBox(scuName, token)
      .then((b) => {
        if (cancelled) return;
        setBox(b);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setBox(null);
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scuName, token]);

  // On-demand box scan detail — fetched only when this page opens, not carried
  // in the every-refresh snapshot. (Photos are loaded by <PhotosViewer/>.)
  const [scan, setScan] = useState<BoxScanDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (!scuName || !token) return;
    let cancelled = false;
    fetchScanDetail(scuName, token)
      .then((d) => !cancelled && setScan(d))
      .catch((e) => !cancelled && setDetailError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [scuName, token]);

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4 flex items-start justify-between">
        <div>
          <Link
            to="/boxes"
            className="text-xs text-primary-500 hover:underline"
          >
            ← back to smartboxes
          </Link>
          <h1 className="text-xl font-bold mt-1">
            <code className="font-mono">{scuName}</code>
          </h1>
          {box && (
            <div className="text-xs text-secondary-500 mt-0.5">
              {fmtCustomerWc(box.customer, box.work_center)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {box && (
            <ReportMaintenanceButton
              scu={box.scu_name}
              label={box.smartbox_code ?? box.scu_name}
              variant="button"
            />
          )}
          <Button asChild variant="outline" size="sm">
            <a
              href={`mailto:?subject=${encodeURIComponent(`MTTR ${scuName}`)}`}
            >
              Share via email
            </a>
          </Button>
        </div>
      </header>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error.message}
        </div>
      )}
      {detailError && (
        <div className="mb-3 text-sm text-warning-800 bg-warning-50 px-3 py-2 rounded">
          Scan/photo detail unavailable: {detailError}
        </div>
      )}

      {loading && !box ? (
        <LoadingSpinner label="loading box detail" />
      ) : !box ? (
        <div className="text-sm text-secondary-500 bg-white p-6 rounded-md border border-secondary-200">
          No box named <code>{scuName}</code> in the current snapshot.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DetailCard title="Classification">
            <DetailRow label="State">
              <StateBadge state={box.state} />
            </DetailRow>
            <DetailRow label="Cause">{box.classification ?? "-"}</DetailRow>
            <DetailRow label="Primary signal">
              {box.primary_signal ?? "-"}
            </DetailRow>
            <DetailRow label="Since">{fmtUtc(box.since_utc)}</DetailRow>
            <DetailRow label="Elapsed">
              <strong>{fmtDuration(box.elapsed_seconds)}</strong>
            </DetailRow>
          </DetailCard>

          <DetailCard title="Ticket">
            {box.open_ticket_id ? (
              <>
                <DetailRow label="Open ticket">
                  <span className="text-primary-700 font-semibold">
                    {box.open_ticket_origin} #{box.open_ticket_id}
                  </span>
                </DetailRow>
                <DetailRow label="Summary">
                  {box.open_ticket_summary ?? "-"}
                </DetailRow>
                <DetailRow label="Assigned to">
                  {box.open_ticket_assignee ?? (
                    <em className="text-secondary-500">unclaimed</em>
                  )}
                </DetailRow>
                <DetailRow label="Comments">
                  {box.open_ticket_comment_count ?? 0}
                </DetailRow>
              </>
            ) : (
              <div className="text-sm text-secondary-500">
                No open ticket on either DB.
              </div>
            )}
          </DetailCard>

          {/* Per-smartbox freeform notes (platform-only; renders nothing when
              the box has none). Placed high so operators see box quirks early. */}
          <div className="lg:col-span-2">
            <BoxNotesCard scu={box.scu_name} />
          </div>

          {/* READER CLUSTER — kept together and high on the page (Jason
              2026-09-16). The per-antenna EPC map, the reader controls and
              the unmatched tags all answer one question: is the reader
              doing its job? Split down the page they had to be correlated
              by scrolling. The antenna map in particular is the first
              thing to check on a box that scans but reads short. */}
          <DetailCard title="Last scan">
            <DetailRow label="Scanned at">
              {scan ? fmtUtc(scan.scan_end_utc) : "…"}
            </DetailRow>
            <DetailRow label="Tags read">{scan?.total_tags ?? "—"}</DetailRow>
{/* Per-antenna EPC counts (platform only — present when the API
                returns them), ONE MAP PER READER. Port numbers are per reader:
                reader 1 port 4 and reader 2 port 4 are different physical
                antennas at opposite ends of the box, so a two-reader box gets
                two maps rather than one longer one. The per-reader heading is
                suppressed when there is only one, which is the whole fleet
                today. A 32-port map is a 20 ft box. */}
            {(() => {
              const groups = readerGroups(scan);
              if (groups.length === 0) {
                return scan && scan.scan_id === null ? (
                  <p className="text-xs text-secondary-500 mt-2">
                    No scans recorded for this box.
                  </p>
                ) : null;
              }
              const multi = groups.length > 1;
              return (
                <div className="mt-2">
                  <div className="text-xs text-secondary-500 mb-1">
                    EPCs per antenna{" "}
                    <span
                      className="text-secondary-400 cursor-help underline decoration-dotted decoration-secondary-300 underline-offset-2"
                      title={
                        "Each tag is stored against the ONE antenna that won best " +
                        "RSSI for it, so these are wins, not every read. A good " +
                        "antenna that is always second-best to its neighbour also " +
                        "shows zero. Upstream is adding a per-tag 32-antenna mask " +
                        "that will turn this into a true 'saw' count."
                      }
                    >
                      (light red = won zero tags — worth a look, not proof of a fault)
                    </span>
                  </div>
                  {groups.map((g) => (
                    <div key={g.reader_id} className="mb-2 last:mb-0">
                      {multi && (
                        <div className="text-xs font-medium text-secondary-600 mb-1">
                          Reader {g.reader_id}{" "}
                          <span className="font-normal text-secondary-400">
                            · {g.antennas.length} port
                            {g.antennas.length === 1 ? "" : "s"},{" "}
                            {g.antennas.reduce((n, a) => n + a.epc_count, 0)} tags
                          </span>
                        </div>
                      )}
                      <div className="grid grid-cols-4 gap-1 text-xs">
                        {g.antennas.map((a) => {
                          const dead = a.epc_count === 0;
                          const label = multi
                            ? `Reader ${g.reader_id} antenna ${a.antenna_number}`
                            : `Antenna ${a.antenna_number}`;
                          return (
                            <div
                              key={a.antenna_number}
                              className={
                                "flex justify-between rounded px-1.5 py-0.5 " +
                                (dead
                                  ? "bg-error-100 border border-error-300"
                                  : "bg-secondary-50")
                              }
                              title={dead ? `${label}: read zero tags` : label}
                            >
                              <span
                                className={dead ? "text-error-600" : "text-secondary-500"}
                              >
                                #{a.antenna_number}
                              </span>
                              <span
                                className={
                                  dead ? "font-semibold text-error-700" : "font-semibold"
                                }
                              >
                                {a.epc_count}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </DetailCard>

          <ReaderCard scuName={box.scu_name} />

          <RebootCard scuName={box.scu_name} />

          <UnmatchedTagsCard scu={box.scu_name} />

          {box.open_ticket_id !== null &&
            box.open_ticket_origin !== null && (
              <div className="lg:col-span-2">
                <TicketActivity
                  origin={box.open_ticket_origin}
                  ticketId={box.open_ticket_id}
                  initialAssignee={box.open_ticket_assignee}
                  box={box}
                />
              </div>
            )}

          <div className="lg:col-span-2">
            <TicketHistoryCard scu={box.scu_name} />
          </div>

          <DetailCard title="Camera">
            <DetailRow label="v2_camera_health last heartbeat">
              {fmtUtc(box.camera_last_heartbeat_utc)}
            </DetailRow>
            <DetailRow label="Cascade source used">
              {box.camera_signal_source ?? "(no signal)"}
            </DetailRow>
            <p className="text-xs text-secondary-500 mt-2">
              Cascade order: v2_camera_health → Mongo → per-SCU local DB. The
              first source with a recent timestamp wins.
            </p>
          </DetailCard>

          <DetailCard title="Peplink">
            <DetailRow label="Status">
              <SignalDot label="Pep" {...deriveSignalDots(box).pep} />
            </DetailRow>
            <DetailRow label="Last online">
              {fmtUtc(box.peplink_last_online_utc)}
            </DetailRow>
            <DetailRow label="Offline at">
              {fmtUtc(box.peplink_offline_at_utc)}
            </DetailRow>
            <DetailRow label="Last polled">
              {fmtUtc(box.peplink_status_polled_utc)}
            </DetailRow>
            <DetailRow label="Last event">
              {box.peplink_last_event_type
                ? `${box.peplink_last_event_type} (${fmtUtc(box.peplink_last_event_utc)})`
                : "-"}
            </DetailRow>
            {box.peplink_address && (
              <p className="text-xs text-secondary-700 mt-2">
                {box.peplink_address}
              </p>
            )}
          </DetailCard>

          <PeplinkClientsCard scu={box.scu_name} />

          <BandwidthCard scuName={box.scu_name} />

          <SimControlCard scuName={box.scu_name} />

          <DetailCard title="Smartplug">
            <DetailRow label="Status">
              <SignalDot label="Plug" {...deriveSignalDots(box).plug} />
            </DetailRow>
            {(box.plug_outlet_count ?? 0) >= 2 ? (
              <>
                {/* The pill IS the state + the toggle — one element,
                    not a text label paired with a button next to it. */}
                <DetailRow label="Outlet 1">
                  <PlugOutletResetButton
                    scu={box.scu_name}
                    outletNum={1}
                    outletOn={box.plug_outlet1_on}
                    outletSinceUtc={box.plug_outlet1_since_utc}
                    smartboxLabel={box.smartbox_code}
                    plugIsOnline={box.plug_is_online}
                    variant="card"
                  />
                </DetailRow>
                <DetailRow label="Outlet 2">
                  <PlugOutletResetButton
                    scu={box.scu_name}
                    outletNum={2}
                    outletOn={box.plug_outlet2_on}
                    outletSinceUtc={box.plug_outlet2_since_utc}
                    smartboxLabel={box.smartbox_code}
                    plugIsOnline={box.plug_is_online}
                    variant="card"
                  />
                </DetailRow>
              </>
            ) : (
              <DetailRow label="Outlet">
                <PlugOutletResetButton
                  scu={box.scu_name}
                  outletNum={1}
                  outletOn={box.plug_outlet1_on}
                  outletSinceUtc={box.plug_on_since_utc ?? box.plug_off_since_utc}
                  smartboxLabel={box.smartbox_code}
                  plugIsOnline={box.plug_is_online}
                  variant="card"
                />
              </DetailRow>
            )}
          </DetailCard>

          <DetailCard title="Tailscale">
            <DetailRow label="Status">
              <SignalDot label="TS" {...deriveSignalDots(box).ts} />
            </DetailRow>
            <DetailRow label="Last seen">
              {fmtUtc(box.tailscale_last_seen_utc)}
            </DetailRow>
          </DetailCard>

          <StarlinkCard box={box} />

          {box.ups_state !== null && (
            <DetailCard title="UPS (apcupsd)">
              <DetailRow label="Status">
                <SignalDot label="UPS" {...deriveSignalDots(box).ups} />
              </DetailRow>
              <DetailRow label="Edge state">{box.ups_state}</DetailRow>
              <DetailRow label="Battery">
                {box.ups_battery_pct !== null
                  ? `${box.ups_battery_pct.toFixed(0)}%`
                  : "-"}
              </DetailRow>
              <DetailRow label="Runtime">
                {box.ups_runtime_min !== null
                  ? `${box.ups_runtime_min.toFixed(0)} min`
                  : "-"}
              </DetailRow>
              <DetailRow label="Line voltage">
                {box.ups_line_voltage_v !== null
                  ? `${box.ups_line_voltage_v.toFixed(0)} V`
                  : "-"}
              </DetailRow>
              <DetailRow label="On battery since">
                {fmtUtc(box.ups_on_battery_since_utc)}
              </DetailRow>
              <DetailRow label="Shore power since">
                {fmtUtc(box.ups_shore_power_since_utc)}
              </DetailRow>
              <DetailRow label="Last reading">
                {fmtUtc(box.ups_last_seen_utc)}
              </DetailRow>
              <DetailRow label="Transport">
                {box.ups_transport_status ?? "-"}
              </DetailRow>
              {box.ups_replace_recommended && (
                <p className="text-xs text-warning-700 mt-2">
                  Battery replacement recommended
                </p>
              )}
            </DetailCard>
          )}

          <DetailCard title="Position">
            <DetailRow label="Latitude">{box.lat ?? "-"}</DetailRow>
            <DetailRow label="Longitude">{box.lon ?? "-"}</DetailRow>
            {box.peplink_address && (
              <DetailRow label="Address">{box.peplink_address}</DetailRow>
            )}
            {box.location && (
              <DetailRow label="Region">{box.location}</DetailRow>
            )}
            {box.speed_kph != null && (
              <DetailRow label="Speed">
                {box.speed_kph.toFixed(1)} kph
                {box.heading_deg != null && (
                  <> @ {box.heading_deg.toFixed(0)}°</>
                )}
              </DetailRow>
            )}
            {box.lat != null && box.lon != null && (
              <div className="flex gap-3 text-xs mt-2">
                <a
                  className="text-primary-600 hover:underline"
                  href={`https://www.google.com/maps/search/?api=1&query=${box.lat},${box.lon}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Google Maps ↗
                </a>
                <a
                  className="text-primary-500 hover:underline"
                  href={`https://www.openstreetmap.org/?mlat=${box.lat}&mlon=${box.lon}#map=8/${box.lat}/${box.lon}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on OSM ↗
                </a>
              </div>
            )}
          </DetailCard>

          <DetailCard title="Fleet mapping">
            <DetailRow label="System">{box.system ?? "-"}</DetailRow>
            <DetailRow label="Region">{box.location ?? "-"}</DetailRow>
            <DetailRow label="Starlink">{box.starlink_name ?? "-"}</DetailRow>
            <DetailRow label="Peplink hardwire">
              {box.peplink_hardwire ?? "-"}
            </DetailRow>
            <DetailRow label="AMS status">{box.ams_status ?? "-"}</DetailRow>
            <p className="text-xs text-secondary-500 mt-2">
              From <code>vw_unified_gen5_mapping_latest</code>. Empty fields
              mean this SCU isn't yet in the upstream fleet snapshot.
            </p>
          </DetailCard>

          <DetailCard title="Drill down">
            <p className="text-sm text-secondary-700">
              For the full per-signal trace (peplink event, plug interval,
              tailscale last_seen, RFID scans + zero-tag streak, every
              threshold value applied, plus the linked outage history), use
              the CLI:
            </p>
            <pre className="mt-2 p-3 bg-secondary-100 rounded text-xs overflow-auto">
{`python -m mttr signal ${box.scu_name}`}
            </pre>
          </DetailCard>

          {/* Photos LAST (Jason 2026-09-16): the heaviest tile on the page and
              the least often why someone opened it. */}
          <div className="lg:col-span-2">
            <PhotosViewer scu={box.scu_name} />
          </div>

        </div>
      )}
    </div>
  );
};


export default SignalDetailPage;
